<template>
  <div></div>
</template>

<script>
import Vue from 'vue';
import { mapState, mapStores, mapActions } from 'pinia';
import { useStore as useShapeStore } from '@/stores/shape';
import { useStore as useActionStore } from '@/stores/action';
import { useStore as useProductStore } from '@/stores/product';
import { useStore as useHistoryStore } from '@/stores/history';
import { useStore as useMirrorStore } from '@/stores/mirror';
import { openDefaultEditor } from 'pintura';
import { createStateSnapshot } from '@/utils/historyHelper';

export default Vue.extend({
  computed: {
    ...mapStores(useShapeStore),
    ...mapStores(useProductStore),
    ...mapStores(useActionStore),
    ...mapState(useShapeStore, ['shapes', 'activeShapeId']),
    ...mapState(useActionStore, ['actions']),
  },
  mounted() {
    const subtarget = document.querySelector('w2p-import-wizard-wrapper');
    let element;

    if (subtarget && subtarget.shadowRoot) {
      element = subtarget.shadowRoot.querySelector('.import-wizard');
    } else {
      const oldVersionTarget = document.querySelector('w2p-import-wizard');
      element =
        oldVersionTarget && oldVersionTarget.shadowRoot
          ? oldVersionTarget.shadowRoot.querySelector('.import-wizard')
          : document.body;
    }

    // in 2.18 version revert to old changes
    // const element =
    //  subtarget && subtarget.shadowRoot
    //    ? subtarget.shadowRoot.querySelector('.import-wizard')

    const activeShape = this.shapes.find(
      (shape) => shape.id === this.activeShapeId
    );
    const productStore = useProductStore();
    const isLaser = productStore.isLaserConverter();
    const isDebossing = productStore.isDebossingTechnique();

    const stickerEditor = openDefaultEditor(
      {
        class: 'pintura-sticker-editor',
        src:
          isLaser || isDebossing
            ? activeShape.pngFileWithoutConverters
            : activeShape.pngFile,
        enableButtonRevert: false,
        enableNavigateHistory: false,
        enableZoomControls: false,
        cropEnableButtonRotateLeft: false,
        cropEnableButtonFlipHorizontal: false,
        cropImageSelectionCornerStyle: 'hook',
        cropEnableCenterImageSelection: false,
        cropActiveTransformTool: 'zoom',
        cropEnableRotationInput: false,
        utils: ['crop'],
        cropInteractionFocus: 'selection',
        locale: { labelButtonExport: this.$t('tools.custom.done') },
        cropEnableInfoIndicator: true,
        cropEnableZoom: false,
      },
      element
    );

    // Update the shape when the sticker is edited
    stickerEditor.on('process', async (res) => {
      // Update the backgroundImage of the active shape
      const actionStore = useActionStore();
      const idx = this.shapeStore.shapes.findIndex(
        (p) => p.id === activeShape?.id
      );

      const shape = this.shapeStore.shapes[idx];
      const widthScaleFactor = shape.pngWidthPixels / res.imageState.crop.width;
      const heightScaleFactor =
        shape.pngHeightPixels / res.imageState.crop.height;
      const width = shape.width / widthScaleFactor;
      const height = shape.height / heightScaleFactor;

      const smallDetailsResult = shape.smallDetailsResult
        ? { ...shape.smallDetailsResult, isCheckValid: false }
        : undefined;

      const leftCroppedValue = this.getCroppedValueFromLeftSide(
        shape.pngWidthPixels,
        res.imageState.crop.x
      );

      const rightCroppedValue = this.getCroppedValueFromRightSide(
        shape.pngWidthPixels,
        res.imageState.crop.x + res.imageState.crop.width
      );

      const topCroppedValue = this.getCroppedValueFromTop(
        shape.pngHeightPixels,
        res.imageState.crop.y
      );

      const bottomCroppedValue = this.getCroppedValueFromBottom(
        shape.pngHeightPixels,
        res.imageState.crop.y + res.imageState.crop.height
      );

      actionStore.createCropAction(this.activeShapeId, {
        left: leftCroppedValue,
        right: rightCroppedValue,
        top: topCroppedValue,
        bottom: bottomCroppedValue,
      });

      this.shapeStore.$patch((state) => {
        state.shapes.splice(idx, 1, {
          ...state.shapes[idx],
          backgroundImage: URL.createObjectURL(res.dest),
          pngFileWithoutConverters: res.dest,
          pngFile: res.dest,
          height: height,
          width: width,
          aspectRatio: width / height,
          pngWidthPixels: res.imageState.crop.width,
          pngHeightPixels: res.imageState.crop.height,
          variantArtworkImages: [],
          smallDetailsResult,
        });
      });

      await this.updateColorsAfterImageIsAdjusted(this.activeShapeId, true);

      if (this.shapes[idx]) {
        await this.applyEffectIfNeeded(
          productStore.selectedPrintPosition.id,
          productStore.selectedPrintPosition.techniqueId,
          this.shapes[idx]
        );
      }

      if (this.shapes[idx]) {
        this.updateMirrorShape(this.shapes[idx]);
      }

      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);

      this.$emit('close:crop-editor');
    });

    stickerEditor.on('close', () => {
      this.$emit('close:crop-editor');
    });
  },
  methods: {
    ...mapActions(useShapeStore, [
      'updateColorsAfterImageIsAdjusted',
      'applyEffectIfNeeded',
    ]),
    ...mapActions(useHistoryStore, ['recordHistory']),
    ...mapActions(useMirrorStore, ['updateMirrorShape']),

    getCroppedValueFromLeftSide(originalWidth, croppedValue) {
      if (croppedValue === 0) {
        return 0;
      }
      //percantage cropped shape value from left side
      return (croppedValue * 100) / originalWidth;
    },
    getCroppedValueFromRightSide(originalWidth, croppedValue) {
      const croppedValuePixels = originalWidth - croppedValue;
      if (croppedValuePixels === 0) {
        return 0;
      }
      //percantage cropped shape value from right side
      return (croppedValuePixels * 100) / originalWidth;
    },
    getCroppedValueFromTop(originalHeight, croppedValue) {
      if (croppedValue === 0) {
        return 0;
      }
      //percantage cropped shape value from top
      return (croppedValue * 100) / originalHeight;
    },
    getCroppedValueFromBottom(originalHeight, croppedValue) {
      const croppedValuePixels = originalHeight - croppedValue;
      if (croppedValuePixels === 0) {
        return 0;
      }
      //percantage cropped shape value from right side
      return (croppedValuePixels * 100) / originalHeight;
    },
  },
});
</script>

<style scoped></style>
